import WebHttpClient from '~src/httpClient/WebHttpClient';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { GetSupportedContentTypesRequest } from '../serverApi/requests/GetSupportedContentTypesRequest';
import { IContentTypeInfo } from '../serverApi/types/IContentTypeInfo';
import { isRelayableItem, relayWireContentType } from './relaySelection';

/**
 * The content types currently supported for adding to a Content Relay plan.
 *
 * The authoritative, growing set is owned by the tabhf-content-mobility service and read from the
 * monolith `listSupportedContentTypes` web method via {@link GetSupportedContentTypesRequest}.
 *
 * The set is cached synchronously because the Actions-menu gating that consumes it (isClickable) runs
 * synchronously and cannot await a request. The cache is seeded with {@link FALLBACK_SUPPORTED_WIRE_TYPES}
 * so the menu works before the first load completes, and keeps that seed if the load fails.
 * {@link loadSupportedContentTypes} is called when the left nav mounts (so the set is loaded before any
 * Actions menu opens) and again from the Explore "Add to Relay Plan" action. Once the set has loaded it
 * is not requested again on the page; after a failed load the next call retries.
 */

/** The initial supported set, used until the server's set is loaded or when loading it fails. */
const FALLBACK_SUPPORTED_WIRE_TYPES: ReadonlyArray<string> = ['PROJECTS', 'WORKBOOKS', 'DATASOURCES'];

/** Synchronous cache of supported wire content-type keys, seeded with the fallback set. */
let cachedWireTypes: ReadonlyArray<string> = FALLBACK_SUPPORTED_WIRE_TYPES;

let loadPromise: Promise<ReadonlyArray<string>> | undefined;

/** The web method returns a bare array; an error comes back as an `{ errors }` object instead. */
function fetchSupportedContentTypes(): Promise<IContentTypeInfo[]> {
  return WebHttpClient.sendRequest(new GetSupportedContentTypesRequest())
    .then(function(result: any) { return Array.isArray(result) ? result : Promise.reject(result); });
}

/**
 * Loads (once) the supported content types and refreshes the synchronous cache. On failure the cache
 * keeps the fallback set and the promise resolves with it, so callers never need a catch; the failed
 * load is forgotten so the next call retries.
 */
export function loadSupportedContentTypes(): Promise<ReadonlyArray<string>> {
  if (!loadPromise) {
    loadPromise = fetchSupportedContentTypes().then(
      function(types) {
        cachedWireTypes = types.map(function(info) { return info.contentType; });
        return cachedWireTypes;
      },
      function() {
        loadPromise = undefined;
        return cachedWireTypes;
      });
  }
  return loadPromise;
}

/** Test-only: forgets any loaded set and restores the fallback, so each test starts from the seed. */
export function resetSupportedContentTypesForTests(): void {
  cachedWireTypes = FALLBACK_SUPPORTED_WIRE_TYPES;
  loadPromise = undefined;
}

/** The currently-known supported wire content-type keys (from cache). */
export function getSupportedWireTypesSync(): ReadonlyArray<string> {
  return cachedWireTypes;
}

/** True when the given wire content-type key is in the supported set. */
export function isWireTypeSupported(wireContentType: string): boolean {
  return !!wireContentType && cachedWireTypes.indexOf(wireContentType) !== -1;
}

/**
 * True when the item can be added to a relay plan right now: it is a client-relayable type and its
 * content type is in the currently-supported set. Layers the dynamic supported set over the static
 * {@link isRelayableItem} mapping.
 */
export function isItemSupportedForRelay(item: IContentInfo): boolean {
  return isRelayableItem(item) && isWireTypeSupported(relayWireContentType(item.contentType));
}
